import { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatRupees, formatRupeesCompact } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, User, Wallet, TrendingUp, Send, Landmark } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const PARTNER_AVATARS = [
  "https://images.unsplash.com/photo-1718209881007-c0ecdfc00f9d?w=200&auto=format&fit=facearea&facepad=2",
  "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=200&auto=format&fit=facearea&facepad=2",
  "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=200&auto=format&fit=facearea&facepad=2",
  "https://images.pexels.com/photos/27086922/pexels-photo-27086922.jpeg?w=200",
  "https://images.pexels.com/photos/11655430/pexels-photo-11655430.jpeg?w=200",
  "https://images.unsplash.com/photo-1614786269829-d24616faf56d?w=200&auto=format&fit=facearea&facepad=2",
  "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=200&auto=format&fit=facearea&facepad=2",
];

export default function Partners() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin";
  const [partners, setPartners] = useState([]);
  const [stmt, setStmt] = useState({});
  const [addOpen, setAddOpen] = useState(false);
  const [distOpen, setDistOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", phone: "", email: "", pan: "", bank_account: "",
    capital_contributed: "", ownership_percent: "", is_external: false, linked_investment_id: "",
  });
  const [dist, setDist] = useState({ total_amount: "", date: new Date().toISOString().slice(0, 10), note: "" });
  const [distPreview, setDistPreview] = useState(null);
  const [dists, setDists] = useState([]);

  const load = async () => {
    const [p, d] = await Promise.all([
      api.get("/partners"),
      api.get("/distributions"),
    ]);
    setPartners(p.data);
    setDists(d.data);
    const s = {};
    for (const pt of p.data) {
      try {
        const r = await api.get(`/partners/${pt.id}/statement`);
        s[pt.id] = r.data;
      } catch { /* ignore */ }
    }
    setStmt(s);
  };

  useEffect(() => { load(); }, []);

  const savePartner = async () => {
    try {
      await api.post("/partners", {
        ...form,
        capital_contributed: Number(form.capital_contributed || 0),
        ownership_percent: Number(form.ownership_percent || 0),
      });
      toast.success("Partner added");
      setAddOpen(false);
      setForm({ name: "", phone: "", email: "", pan: "", bank_account: "",
        capital_contributed: "", ownership_percent: "", is_external: false, linked_investment_id: "" });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const previewDist = () => {
    const internals = partners.filter((p) => !p.is_external);
    const totalPct = internals.reduce((a, p) => a + p.ownership_percent, 0);
    if (totalPct <= 0) { toast.error("Partners have no ownership set"); return; }
    setDistPreview(internals.map((p) => ({
      name: p.name, percent: p.ownership_percent,
      amount: Number(dist.total_amount || 0) * p.ownership_percent / totalPct,
    })));
  };

  const saveDist = async () => {
    try {
      await api.post("/distributions", { ...dist, total_amount: Number(dist.total_amount) });
      toast.success("Distribution recorded");
      setDistOpen(false);
      setDistPreview(null);
      setDist({ total_amount: "", date: new Date().toISOString().slice(0, 10), note: "" });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const internals = partners.filter((p) => !p.is_external);
  const externals = partners.filter((p) => p.is_external);
  const totalOwnership = internals.reduce((a, p) => a + p.ownership_percent, 0);
  const totalCapital = internals.reduce((a, p) => a + p.capital_contributed, 0);

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow">Ownership</div>
          <h1 className="mt-2 font-editorial font-light text-[38px] leading-[1.02] sm:text-5xl md:text-6xl text-slate-900 tracking-[-0.035em]" data-testid="partners-title">
            <em className="italic font-light">Partners.</em>
          </h1>
          <p className="text-sm text-slate-500 mt-3">
            {internals.length} internal partners · {totalOwnership.toFixed(2)}% ownership · {formatRupeesCompact(totalCapital)} capital
          </p>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <Button onClick={() => setAddOpen(true)} variant="outline" className="rounded-none gap-2" data-testid="add-partner-btn">
              <Plus className="h-4 w-4" /> Add Partner
            </Button>
            <Button onClick={() => setDistOpen(true)} className="rounded-none bg-red-600 hover:bg-red-700 text-white gap-2" data-testid="distribute-btn">
              <Send className="h-4 w-4" /> Distribute Profit
            </Button>
          </div>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {internals.map((p, i) => {
          const s = stmt[p.id];
          return (
            <div key={p.id} className="border border-slate-200 bg-white p-5 hover-lift" data-testid={`partner-card-${p.id}`}>
              <div className="flex items-center gap-3">
                <img
                  src={p.photo_url || PARTNER_AVATARS[i % PARTNER_AVATARS.length]}
                  alt={p.name}
                  className="h-12 w-12 object-cover border border-slate-200 grayscale hover:grayscale-0 transition-all"
                  onError={(e) => { e.target.style.display = "none"; }}
                />
                <div className="min-w-0">
                  <h3 className="font-display font-bold text-lg text-slate-900 truncate">{p.name}</h3>
                  <div className="text-xs text-slate-500 truncate">{p.email}</div>
                </div>
                <div className="ml-auto text-right">
                  <div className="text-[10px] tracking-wider uppercase text-slate-400">Owns</div>
                  <div className="font-mono-fin font-bold text-lg text-red-600">{p.ownership_percent}%</div>
                </div>
              </div>
              {s && (
                <div className="grid grid-cols-2 gap-3 mt-5 pt-4 border-t border-slate-100">
                  <div>
                    <div className="text-[10px] tracking-wider uppercase text-slate-400">Capital in</div>
                    <div className="font-mono-fin text-sm text-slate-900 mt-0.5">{formatRupeesCompact(s.money_in)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] tracking-wider uppercase text-slate-400">Share value</div>
                    <div className="font-mono-fin text-sm text-slate-900 mt-0.5">{formatRupeesCompact(s.share_value_today)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] tracking-wider uppercase text-slate-400">Profit</div>
                    <div className={`font-mono-fin text-sm mt-0.5 ${s.profit >= 0 ? "text-green-700" : "text-red-600"}`}>{formatRupeesCompact(s.profit)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] tracking-wider uppercase text-slate-400">Paid out</div>
                    <div className="font-mono-fin text-sm text-slate-900 mt-0.5">{formatRupeesCompact(s.paid_out)}</div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {externals.length > 0 && (
        <div>
          <h2 className="font-display font-bold text-xl text-slate-900 mb-3">External investors</h2>
          <div className="grid gap-4 md:grid-cols-2">
            {externals.map((p) => (
              <div key={p.id} className="border border-slate-200 bg-white p-4 flex items-center gap-3">
                <Landmark className="h-5 w-5 text-slate-500" />
                <div>
                  <div className="font-medium">{p.name}</div>
                  <div className="text-xs text-slate-500">Linked to a specific project · {formatRupees(p.capital_contributed)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {dists.length > 0 && (
        <div>
          <h2 className="font-display font-bold text-xl text-slate-900 mb-3">Recent distributions</h2>
          <div className="border border-slate-200 bg-white">
            {dists.slice(0, 5).map((d) => (
              <div key={d.id} className="flex items-center justify-between p-4 border-b border-slate-100 last:border-0">
                <div>
                  <div className="font-medium text-slate-900">{formatRupees(d.total_amount)}</div>
                  <div className="text-xs text-slate-500">{d.date} · {d.note || "Profit distribution"}</div>
                </div>
                <div className="text-xs text-slate-500">{d.splits.length} partners</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add partner */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add partner</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2" data-testid="pn-name" /></div>
              <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2" data-testid="pn-email" /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-2" /></div>
              <div><Label>PAN</Label><Input value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value })} className="mt-2" /></div>
              <div><Label>Bank account</Label><Input value={form.bank_account} onChange={(e) => setForm({ ...form, bank_account: e.target.value })} className="mt-2" /></div>
              <div><Label>Capital (Rs)</Label><Input type="number" value={form.capital_contributed} onChange={(e) => setForm({ ...form, capital_contributed: e.target.value })} className="mt-2" data-testid="pn-capital" /></div>
              <div><Label>Ownership (%)</Label><Input type="number" value={form.ownership_percent} onChange={(e) => setForm({ ...form, ownership_percent: e.target.value })} className="mt-2" data-testid="pn-owner" /></div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={form.is_external} onCheckedChange={(v) => setForm({ ...form, is_external: !!v })} data-testid="pn-external" />
              External investor (belongs to a specific project only)
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} className="rounded-none">Cancel</Button>
            <Button onClick={savePartner} className="rounded-none bg-red-600 hover:bg-red-700 text-white" data-testid="pn-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Distribute */}
      <Dialog open={distOpen} onOpenChange={(o) => { setDistOpen(o); if (!o) setDistPreview(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Distribute profit to partners</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Total amount (Rs)</Label><Input type="number" value={dist.total_amount} onChange={(e) => setDist({ ...dist, total_amount: e.target.value })} className="mt-2" data-testid="dist-amount" /></div>
            <div><Label>Date</Label><Input type="date" value={dist.date} onChange={(e) => setDist({ ...dist, date: e.target.value })} className="mt-2" /></div>
            <div><Label>Note</Label><Input value={dist.note} onChange={(e) => setDist({ ...dist, note: e.target.value })} className="mt-2" /></div>
            <Button variant="outline" onClick={previewDist} className="rounded-none w-full" data-testid="dist-preview">Preview split</Button>
            {distPreview && (
              <div className="border border-slate-200 max-h-64 overflow-auto">
                {distPreview.map((s, i) => (
                  <div key={i} className="flex items-center justify-between p-3 border-b border-slate-100 last:border-0 text-sm">
                    <span>{s.name} <span className="text-slate-400">({s.percent}%)</span></span>
                    <span className="font-mono-fin font-medium">{formatRupees(s.amount)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDistOpen(false)} className="rounded-none">Cancel</Button>
            <Button onClick={saveDist} disabled={!dist.total_amount} className="rounded-none bg-red-600 hover:bg-red-700 text-white" data-testid="dist-save">Approve &amp; Mark Paid</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
